import React from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import WorkIcon from '@mui/icons-material/Work';
import RadarIcon from '@mui/icons-material/Radar';
import LabelIcon from '@mui/icons-material/Label';
import PersonIcon from '@mui/icons-material/Person';
import PunchClock from '@mui/icons-material/PunchClock';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import ListIcon from '@mui/icons-material/List';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RateReviewIcon from '@mui/icons-material/RateReview';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SecurityIcon from '@mui/icons-material/Security';
import CategoryIcon from '@mui/icons-material/Category';
import PolicyIcon from '@mui/icons-material/Policy';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import LinkIcon from '@mui/icons-material/Link';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EventIcon from '@mui/icons-material/Event';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';

const navigationItems = [
  {
    id: 'technology-radar',
    title: 'Configure Technology Radar',
    description: 'Set up your technology radar',
    icon: <RadarIcon />,
    path: '/tech-radar',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.TECHNOLOGY_RADAR
    }
  },
  {
    id: 'employee-radar',
    title: 'Configure My Skills',
    description: 'Manage your skills radar',
    icon: <PersonIcon />,
    path: '/employee-radar',
    disabled: false,
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.EMPLOYEE_RADAR
    }
  },
  {
    id: 'tags',
    title: 'Configure Tags',
    description: 'Manage and organize your tags',
    icon: <LabelIcon />,
    path: '/tags',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.TAG
    }
  },
  {
    id: 'technology-reconciliation',
    title: 'Technology Reconciliation',
    description: 'Reconcile custom technologies with standard radar segments',
    icon: <LinkIcon />,
    path: '/tech-radar/technology-reconciliation',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.TECHNOLOGY_RECONCILIATION
    }
  },
  {
    id: 'projects',
    title: 'Projects Repository',
    description: 'Set up and manage your projects and dashboards',
    icon: <WorkIcon />,
    path: '/projects',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.PROJECT_REPOSITORY
    }
  },
  {
    id: 'organization',
    title: 'Configure Organization',
    description: 'Manage organization structure and settings',
    icon: <BusinessIcon />,
    path: '/organization',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.ORGANIZATION_CONFIGURATION
    }
  },
  {
    id: 'employees-repository',
    title: 'Individuals Repository',
    description: 'View and manage individual information',
    icon: <PeopleIcon />,
    path: '/employees',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.EMPLOYEE_REPOSITORY
    }
  },
  {
    id: 'skills-matrix',
    title: 'Skills Matrix Configuration',
    description: 'Configure skills matrix templates and skills',
    icon: <SettingsIcon />,
    path: '/skills-matrix',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.SKILLS_MATRIX
    }
  },
  {
    id: 'cv-configuration',
    title: 'CV Configuration',
    description: 'Configure CV options like education, languages, and certifications',
    icon: <HistoryEduIcon />,
    path: '/cv-configuration',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.CV_CONFIGURATION
    }
  },
  {
    id: 'cv-repository',
    title: 'CV Repository',
    description: 'View and manage individual CVs',
    icon: <DescriptionIcon />,
    path: '/cv-repository',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.CV_REPOSITORY
    }
  },
  {
    id: 'feedback-sessions',
    title: 'Feedback Sessions',
    description: 'Manage and view feedback sessions',
    icon: <AssessmentIcon />,
    path: '/feedback-sessions',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.FEEDBACK_SESSION
    }
  },
  {
    // ADR-095 — sub-item of Feedback Sessions, mirroring Project Type
    // Policies under Projects Repository
    id: 'assignment-completion-policies',
    title: 'Assignment Completion Policies',
    description: 'Define expected assignments per cluster',
    icon: <PolicyIcon />,
    path: '/feedback-sessions/assignment-completion-policies',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.ASSIGNMENT_COMPLETION_POLICY
    }
  },
  {
    // ADR-108 — sibling of the policies: WHO is measured (scope), where the
    // policies say WHAT is expected (targets). Same permission on purpose.
    id: 'assignment-completion-exclusions',
    title: 'Assignment Completion Exclusions',
    description: 'Engagement models not measured by the Assignment Completion report',
    icon: <PersonOffIcon />,
    path: '/feedback-sessions/assignment-completion-exclusions',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.ASSIGNMENT_COMPLETION_POLICY
    }
  },
  {
    id: 'feedback-results',
    title: 'Feedback Results',
    description: 'View feedback session and feedback on demand results',
    icon: <AssessmentIcon />,
    path: '/feedback-results',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.FEEDBACK_RESULT
    }
  },
  {
    id: 'individual-assignments',
    title: 'Individual Assignments',
    description: 'Manage individual feedback assignments',
    icon: <AssessmentIcon />,
    path: '/individual-assignments',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.INDIVIDUAL_ASSIGNMENT
    }
  },
  {
    id: 'my-assignments',
    title: 'Assignments',
    description: 'View your assignments',
    icon: <ListIcon />,
    path: '/my-assignments',
  },
  {
    id: 'my-feedback-evaluators',
    title: 'Configure Feedback Evaluators',
    description: 'Manage your feedback evaluators',
    icon: <PeopleIcon />,
    path: '/my-feedback-evaluators',
  },
  {
    id: 'my-feedback-actions',
    title: 'Provide Soft Skills Feedback Actions',
    description: 'Provide and manage feedback actions for your colleagues as the final step of the feedback process.',
    icon: <RateReviewIcon />,
    path: '/my-feedback-actions',
  },
  {
    id: 'my-instant-feedbacks',
    title: 'Instant Feedbacks',
    description: 'View your instant feedbacks. You can send instant feedbacks to your colleagues, visible only by you and them.',
    icon: <ChatBubbleOutlineIcon />,
    path: '/my-instant-feedbacks',
  },
  {
    id: 'my-feedback-results',
    title: 'Feedback Results',
    description: 'View your feedback session and feedback on demand results',
    icon: <AssessmentIcon />,
    path: '/my-feedback-results',
  },
  {
    id: 'my-cv',
    title: 'My CVs',
    description: 'View and manage your CVs',
    icon: <DescriptionIcon />,
    path: '/cv-repository/my-cvs',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.MY_CV
    }
  },
  {
    id: 'my-idp',
    title: 'My Development Plans',
    description: 'View your individual development plans and goals',
    icon: <TrendingUpIcon />,
    path: '/my-idps',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.MY_IDP
    }
  },
  {
    id: 'absences',
    title: 'My Availability',
    description: 'View and manage your availability',
    icon: <CalendarIcon />,
    path: '/availability',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.ABSENCES
    }
  },
  {
    id: 'timesheets',
    title: 'My Timesheets',
    description: 'View and manage your timesheets',
    icon: <PunchClock />,
    path: '/timesheets',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.TIMESHEETS
    }
  },
  {
    id: 'invoices',
    title: 'My B2B',
    description: 'View and manage your invoices',
    icon: <InsertDriveFileIcon />,
    path: '/settlements',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.INVOICES
    }
  },
  {
    id: 'sows',
    title: 'My Intercompany SOWs',
    description: 'View and manage intercompany sows',
    icon: <HandshakeIcon />,
    path: '/sows',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.SOWS
    }
  },
  {
    id: 'orders',
    title: 'My Orders',
    description: 'View and manage your orders',
    icon: <ShoppingCartIcon />,
    path: '/orders',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.ORDERS
    }
  },
  {
    id: 'business-trips',
    title: 'My Trips',
    description: 'View and manage your trips',
    icon: <FlightTakeoffIcon />,
    path: '/trips',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.BUSINESS_TRIPS
    }
  },
  {
    id: 'terms',
    title: 'My Terms',
    description: 'View your terms and conditions',
    icon: <DescriptionIcon />,
    path: '/terms',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.TERMS
    }
  },
  {
    id: 'customer-orders',
    title: 'My Clients',
    description: 'Manage customer orders and billing',
    icon: <BusinessIcon />,
    path: '/customer-orders',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.CUSTOMER_ORDERS
    }
  },
  {
    id: 'reports',
    title: 'Reports',
    description: 'View and manage your reports',
    icon: <AssessmentIcon />,
    path: '/reports',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.REPORTS
    }
  },
  {
    id: 'data-import',
    title: 'Data Import',
    description: 'Import data from external sources',
    icon: <UploadFileIcon />,
    path: '/data-import',
    permission: {
      action: ACTIONS.CREATE,
      subject: SUBJECTS.DATA_IMPORT
    }
  },
  {
    id: 'project-purposes',
    title: 'Project Purposes',
    description: 'Manage project purposes',
    icon: <CategoryIcon />,
    path: '/projects/purposes',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.PROJECT_PURPOSE
    }
  },
  {
    id: 'project-type-policies',
    title: 'Project Type Policies',
    description: 'Configure project type policies for organization units',
    icon: <PolicyIcon />,
    path: '/projects/type-policies',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.PROJECT_TYPE_POLICY
    }
  },
  {
    // ADR-132: ingestion configuration (job position mappings; sync runs next)
    id: 'ingestion',
    title: 'Ingestion',
    description: 'Configure the Americas and PowerBI syncs',
    icon: <SyncAltIcon />,
    path: '/ingestion',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.INGESTION_CONFIGURATION
    }
  },
  {
    id: 'permissions',
    title: 'Permissions',
    description: 'Manage roles and permissions',
    icon: <SecurityIcon />,
    path: '/permissions',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.PERMISSION
    }
  },
  {
    id: 'idp-repository',
    title: 'Individual Development Plans',
    description: 'Track and manage individual development goals and progress',
    icon: <TrendingUpIcon />,
    path: '/idp',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.IDP_REPOSITORY
    }
  },
  {
    id: 'idp-configuration',
    title: 'IDP Configuration',
    description: 'Configure IDP options like goal statuses, measurement types, and templates',
    icon: <SettingsIcon />,
    path: '/idp-configuration',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.IDP_CONFIGURATION
    }
  },
  {
    id: 'placement-activity-types',
    title: 'Placement Activity Types',
    description: 'Configure placement activity type lookup values',
    icon: <AssignmentIcon />,
    path: '/employees/placement-activity-types',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.PLACEMENT_ACTIVITY_TYPE,
    }
  },
  {
    id: 'placement-status-types',
    title: 'Placement Status Types',
    description: 'Configure placement status type lookup values',
    icon: <AssignmentIcon />,
    path: '/employees/placement-status-types',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.PLACEMENT_ACTIVITY_TYPE,
    }
  },
  {
    id: 'meetings-repository',
    title: 'Check-ins (1:1 Meetings)',
    description: 'Track and manage 1:1 meetings, check-ins, and follow-ups with your team',
    icon: <EventIcon />,
    path: '/meetings',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.MEETING_REPOSITORY
    }
  },
  {
    id: 'meetings-configuration',
    title: 'Meetings Configuration',
    description: 'Configure meeting types, statuses, and talking points',
    icon: <SettingsIcon />,
    path: '/meetings-configuration',
    permission: {
      action: ACTIONS.MANAGE,
      subject: SUBJECTS.MEETING_CONFIGURATION
    }
  },
  {
    id: 'my-meetings',
    title: 'My Meetings',
    description: 'View your upcoming and past 1:1 meetings',
    icon: <EventIcon />,
    path: '/my-meetings',
    permission: {
      action: ACTIONS.READ,
      subject: SUBJECTS.MY_MEETING
    }
  }
];

export default navigationItems;

// Helper functions for navigation groups
export const getNavigationGroups = (ability) => {
  // Helper function to filter items based on permissions
  const filterByPermission = (items) => {
    return items.filter(item => {
      // Always include divider items
      if (item.isDivider) {
        return true;
      }
      if (item.permission) {
        return ability.can(item.permission.action, item.permission.subject);
      }
      return true;
    });
  };

  // Filter navigation items based on permissions
  const getFilteredItems = (itemIds) => {
    const items = itemIds.map(id => {
      // Handle delimiter token
      if (id === '|') {
        return { id: `divider-${Date.now()}-${Math.random()}`, isDivider: true };
      }
      return navigationItems.find(item => item.id === id);
    }).filter(Boolean);

    return filterByPermission(items);
  };

  // Helper function to get a single item with its subitems
  const getItemWithSubitems = (itemId, subitemIds) => {
    const item = navigationItems.find(item => item.id === itemId);
    if (!item) return null;
    
    // Check item permission
    if (item.permission && !ability.can(item.permission.action, item.permission.subject)) {
      return null;
    }
    
    return {
      ...item,
      subitems: filterByPermission(
        navigationItems.filter(item => subitemIds.includes(item.id))
      )
    };
  };

  const navigationGroups = [
    {
      title: 'My Activities',
      items: [
        ...getFilteredItems(['my-assignments', 'my-feedback-evaluators', 'my-feedback-actions', 'my-feedback-results', '|', 'my-instant-feedbacks', 'my-cv', 'my-idp', 'my-meetings', 'absences', 'timesheets', 'invoices', 'sows', 'orders', 'business-trips', 'terms', 'customer-orders'])
      ]
    },
    {
      title: 'Radar Configuration',
      items: [
        // Only include the technology-radar item if it passes permission check
        ...((() => {
          const itemWithSubitems = getItemWithSubitems('technology-radar', ['tags', 'technology-reconciliation']);
          return itemWithSubitems ? [itemWithSubitems] : [];
        })()),
        ...getFilteredItems(['employee-radar'])
      ]
    },
    {
      title: 'Project Management',
      items: [
        ...((() => {
          const itemWithSubitems = getItemWithSubitems('projects', ['project-purposes', 'project-type-policies']);
          return itemWithSubitems ? [itemWithSubitems] : [];
        })())
      ]
    },
    {
      title: 'Organization',
      items: [
        ...getFilteredItems(['organization']),
        ...((() => {
          const itemWithSubitems = getItemWithSubitems('employees-repository', ['placement-activity-types', 'placement-status-types']);
          return itemWithSubitems ? [itemWithSubitems] : [];
        })()),
        ...getFilteredItems(['skills-matrix', 'cv-configuration', 'cv-repository'])
      ]
    },
    {
      title: 'Performance Management',
      items: [
        // Feedback Sessions carries its policies as a sub-item (ADR-095),
        // the same shape Projects Repository uses for Project Type Policies
        ...((() => {
          const itemWithSubitems = getItemWithSubitems('feedback-sessions', ['assignment-completion-policies', 'assignment-completion-exclusions']);
          return itemWithSubitems ? [itemWithSubitems] : [];
        })()),
        ...getFilteredItems(['individual-assignments', 'feedback-results'])
      ]
    },
    {
      title: 'Performance & Growth',
      items: [
        ...((() => {
          const itemWithSubitems = getItemWithSubitems('idp-repository', ['idp-configuration']);
          return itemWithSubitems ? [itemWithSubitems] : [];
        })()),
        ...((() => {
          const itemWithSubitems = getItemWithSubitems('meetings-repository', ['meetings-configuration']);
          return itemWithSubitems ? [itemWithSubitems] : [];
        })())
      ]
    },
    {
      title: 'Analytics & Reports',
      permission: {
        action: ACTIONS.READ,
        subject: SUBJECTS.ANALYTICS
      },
      items: [
        ...getFilteredItems(['reports'])
      ]
    },
    {
      title: 'MISC',
      items: [
        ...getFilteredItems(['data-import', 'ingestion', 'permissions'])
      ]
    }
  ];

  // Filter navigation groups based on permissions
  return navigationGroups.filter(group => {
    if (group.permission) {
      return ability.can(group.permission.action, group.permission.subject);
    }
    return group.items.length > 0;
  });
}; 
